// ── Transform sets ──────────────────────────────────────────────────────────

export const RANGE_TRANSFORMS = new Set([
  'avg',
  'change',
  'change_pct',
  'count',
  'first_value',
  'last_value',
  'max',
  'median',
  'min',
  'sum',
]);

export const POINT_TRANSFORMS = new Set([
  'nearest',
  'exact',
  'last_before',
  'first_after',
  'avg_around',
]);

const KNOWN_TRANSFORMS = new Set([...RANGE_TRANSFORMS, ...POINT_TRANSFORMS]);

export const GROUP_AGG_METHODS = ['avg', 'sum', 'min', 'max', 'median', 'count'];

// ── Internal helpers ────────────────────────────────────────────────────────

function splitSource(source = '') {
  const trimmed = String(source || '').trim();
  const separator = trimmed.lastIndexOf('.');
  if (separator <= 0 || separator >= trimmed.length - 1) {
    return { datasetId: '', seriesId: '' };
  }

  return {
    datasetId: trimmed.slice(0, separator),
    seriesId: trimmed.slice(separator + 1),
  };
}

function normalizeFilterValue(value) {
  if (Array.isArray(value)) {
    return value.filter(Boolean);
  }
  if (value === undefined || value === null || value === '') {
    return [];
  }
  return [value];
}

// ── Parse ───────────────────────────────────────────────────────────────────

export function parseKpiQuery(query = '') {
  const segments = String(query || '')
    .split('|')
    .map((segment) => segment.trim())
    .filter(Boolean);

  const source = splitSource(segments[0] || '');
  const parsed = {
    query: String(query || '').trim(),
    datasetId: source.datasetId,
    seriesId: source.seriesId,
    time: null,
    transform: null,
    referenceOffset: null,
    filters: {},
    categories: null,
    groups: null,
    groupBy: null,
    groupAggregations: [],
  };

  for (const segment of segments.slice(1)) {
    if (segment.startsWith('range(') && segment.endsWith(')')) {
      parsed.time = {
        mode: 'range',
        value: segment.slice(6, -1).trim(),
      };
      continue;
    }

    if (segment.startsWith('at(') && segment.endsWith(')')) {
      parsed.time = {
        mode: 'point',
        value: segment.slice(3, -1).trim(),
      };
      continue;
    }

    if (segment.startsWith('ref(') && segment.endsWith(')')) {
      parsed.referenceOffset = segment.slice(4, -1).trim();
      continue;
    }

    if (segment.startsWith('group_by(') && segment.endsWith(')')) {
      parsed.groupBy = segment.slice(9, -1).trim();
      continue;
    }

    if (segment.startsWith('group_agg(') && segment.endsWith(')')) {
      parsed.groupAggregations = parseGroupAgg(segment.slice(10, -1).trim());
      continue;
    }

    if (segment.startsWith('category=')) {
      parsed.categories = segment
        .slice(9)
        .split(',')
        .map((v) => v.trim())
        .filter(Boolean);
      continue;
    }

    if (segment.startsWith('group=')) {
      parsed.groups = segment
        .slice(6)
        .split(',')
        .map((v) => v.trim())
        .filter(Boolean);
      continue;
    }

    if (KNOWN_TRANSFORMS.has(segment)) {
      parsed.transform = segment;
      continue;
    }

    if (segment.includes('=')) {
      const [filterId, rawValues] = segment.split('=', 2);
      if (!filterId || !rawValues) {
        continue;
      }
      parsed.filters[filterId.trim()] = rawValues
        .split(',')
        .map((value) => value.trim())
        .filter(Boolean);
    }
  }

  return parsed;
}

// ── Build ───────────────────────────────────────────────────────────────────

export function buildKpiQuery({
  datasetId,
  seriesId,
  time = null,
  transform = null,
  referenceOffset = null,
  filters = {},
  categories = null,
  groups = null,
  groupBy = null,
  groupAggregations = [],
} = {}) {
  if (!datasetId || !seriesId) {
    return '';
  }

  const segments = [`${datasetId}.${seriesId}`];

  if (time?.mode === 'point' && time.value) {
    segments.push(`at(${time.value})`);
  } else if (time?.value) {
    segments.push(`range(${time.value})`);
  }

  if (categories && categories.length > 0) {
    segments.push(`category=${categories.join(',')}`);
  }

  if (groups && groups.length > 0) {
    segments.push(`group=${groups.join(',')}`);
  }

  Object.entries(filters || {}).forEach(([filterId, rawValue]) => {
    const values = normalizeFilterValue(rawValue);
    if (!filterId || values.length === 0) {
      return;
    }
    segments.push(`${filterId}=${values.join(',')}`);
  });

  if (groupAggregations && groupAggregations.length > 0) {
    segments.push(serializeGroupAgg(groupAggregations));
  }

  if (transform) {
    segments.push(transform);
  }

  if (referenceOffset) {
    segments.push(`ref(${referenceOffset})`);
  }

  if (groupBy) {
    segments.push(`group_by(${groupBy})`);
  }

  return segments.join(' | ');
}

// ── Update (parse → merge → build) ─────────────────────────────────────────

export function updateKpiQuery(query = '', overrides = {}) {
  const parsed = parseKpiQuery(query);
  const nextTime =
    overrides.time ||
    parsed.time || {
      mode: 'range',
      value: 'today',
    };

  // Use !== undefined so that explicit null overrides actually clear the field
  // (the ?? operator treats null as "no value" and would fall through to parsed)
  return buildKpiQuery({
    datasetId: overrides.datasetId !== undefined ? overrides.datasetId : parsed.datasetId,
    seriesId: overrides.seriesId !== undefined ? overrides.seriesId : parsed.seriesId,
    time: nextTime,
    transform: overrides.transform !== undefined ? overrides.transform : (parsed.transform ?? null),
    referenceOffset: overrides.referenceOffset !== undefined ? overrides.referenceOffset : (parsed.referenceOffset ?? null),
    filters: overrides.filters !== undefined ? overrides.filters : (parsed.filters ?? {}),
    categories: overrides.categories !== undefined ? overrides.categories : (parsed.categories ?? null),
    groups: overrides.groups !== undefined ? overrides.groups : (parsed.groups ?? null),
    groupBy: overrides.groupBy !== undefined ? overrides.groupBy : (parsed.groupBy ?? null),
    groupAggregations: overrides.groupAggregations !== undefined ? overrides.groupAggregations : (parsed.groupAggregations ?? []),
  });
}

// ── Group aggregation helpers ───────────────────────────────────────────────

export function parseGroupAgg(expr = '') {
  return expr
    .split(',')
    .map((pair) => pair.trim())
    .filter((pair) => pair.includes(':'))
    .map((pair) => {
      const [filterId, method] = pair.split(':', 2);
      return { filterId: filterId.trim(), method: method.trim() };
    });
}

export function serializeGroupAgg(aggregations = []) {
  if (!aggregations || aggregations.length === 0) return '';
  const entries = aggregations.map((agg) => `${agg.filterId}:${agg.method}`);
  return `group_agg(${entries.join(',')})`;
}
